import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  Activity,
  ArrowRight,
  Award,
  Bandage,
  BedDouble,
  ChevronRight,
  Clock,
  Dumbbell,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Shirt,
  Sparkles,
  UserRound,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/breast-reduction-surgery-mumbai";

const BREAST_REDUCTION_FAQS = [
  {
    question: "How much can breast size be reduced in one surgery?",
    answer:
      "This depends on your anatomy and goals. Dr. Jain plans the reduction in grams of tissue removed and discusses the expected change in breast size and cup size during consultation. Most patients achieve a 1 to 3 cup size reduction in a single procedure. Very large reductions in patients with gigantomastia may require staged surgery in some cases.",
  },
  {
    question: "Will breast reduction relieve my back and shoulder pain?",
    answer:
      "In the majority of cases, yes. Back pain, neck strain, and shoulder groove pain caused by breast weight resolve or significantly improve after reduction because the mechanical load causing them is removed. Pain relief is often reported within the first few weeks of recovery as the body adjusts to the reduced weight.",
  },
  {
    question: "Can I breastfeed after breast reduction surgery?",
    answer:
      "In many cases, yes. Whether breastfeeding is preserved depends on the technique used and how much glandular tissue and ductal anatomy is maintained. Dr. Jain uses pedicle techniques that preserve the nipple's connection to underlying tissue. Patients who plan to breastfeed after surgery should discuss this explicitly during consultation so the reduction plan accounts for it.",
  },
  {
    question: "How visible are the scars after breast reduction?",
    answer:
      "Scar extent depends on the technique. The anchor technique produces the most scar length, including a horizontal component in the inframammary fold. All scars are positioned to be hidden under bras, swimwear, and most clothing. Scars fade significantly over 12 to 18 months. Silicone sheeting and sun protection during healing improve scar appearance.",
  },
  {
    question: "Is breast reduction painful?",
    answer:
      "Surgery is performed under general anesthesia. Post-operative discomfort is mild to moderate in the first week and managed with medication. Most patients describe a sensation of tightness and soreness rather than sharp pain. Many patients report that the relief from chronic breast-weight pain outweighs the post-operative discomfort within the first few days.",
  },
  {
    question: "When can I return to work after breast reduction?",
    answer:
      "Most patients return to desk work within 1 to 2 weeks. Jobs involving physical labor, lifting, or overhead arm work require a longer break of 4 to 6 weeks. Exercise resumes progressively from weeks 4 to 6 onward.",
  },
  {
    question: "Will my breast size change again after reduction?",
    answer:
      "Significant weight gain adds fat to breast tissue and can increase size after reduction. Pregnancy can also increase volume. Aging and gravity continue to affect breast position over time. Maintaining a stable weight and wearing proper support extends the result. For patients who are post-menopausal or at a stable weight without plans for pregnancy, results are very long-lasting.",
  },
  {
    question: "Is breast reduction covered by insurance in India?",
    answer:
      "Yes — in some cases. In some cases where macromastia is causing documented physical symptoms, insurance coverage for breast reduction is possible. This requires medical documentation of symptoms and in some cases specialist referrals. Dr. Jain's team can advise on the documentation required to support an insurance claim for functional breast reduction.",
  },
];

const breastReductionStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Breast reduction surgery (reduction mammoplasty)",
      alternateName: ["Reduction mammoplasty", "Breast reduction", "Macromastia surgery"],
      description:
        "Surgical removal of excess breast tissue, fat, and skin to reduce size and weight, relieve symptomatic macromastia (neck, back, shoulder pain; rashes; activity limitation), and reshape the breast with pedicle-based nipple repositioning. Dual role: aesthetic proportion and medically indicated functional relief.",
      procedureType: "Surgical",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      bodyLocation: {
        "@type": "AnatomicalStructure",
        name: "Breast",
      },
      followup:
        "Week 1: swelling, support bra, symptom relief often early. Week 2: desk work. Weeks 4–6: routine; avoid heavy lifting. Months 2–3: final size/shape; scars mature 12–18 months.",
      howPerformed:
        "General anesthesia; upright markings; anchor, vertical, or selected liposuction-only approach; tissue and skin resection; pedicle nipple-areola repositioning; layered closure; drains optional; 2.5–4 hours; often overnight stay.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical" },
        {
          "@type": "PropertyValue",
          name: "Functional indication",
          value: "Symptomatic macromastia — pain, grooves, intertrigo, activity restriction",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: BREAST_REDUCTION_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

function BreastReductionJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breastReductionStructuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Breast", href: "/#services" },
    { name: "Breast Reduction", href: "/breast-reduction-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Breast</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Breast Reduction Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Reduction Mammoplasty by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Breast Reduction" icon="🌸" size="hero"  imageSrc={HERO.breastReduction} imageAlt="Breast reduction"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What is Breast Reduction Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Breast reduction surgery, clinically called reduction mammoplasty, removes excess breast tissue, fat, and skin to
              reduce the size and weight of overly large breasts, reshape them into a more proportionate contour, and
              reposition the nipple to a more youthful level. It is one of the few cosmetic procedures that is simultaneously a
              medical intervention. Most patients who seek it have been living with physical symptoms for years before they
              consider surgery as a solution.
            </p>
            <p>
              Large breasts, a condition called macromastia, create a specific pattern of physical problems: chronic neck,
              shoulder, and upper back pain from the weight pulling the spine forward, deep shoulder grooves from bra straps
              bearing that load, skin rashes and infections in the inframammary fold where skin sits against skin, and restricted
              ability to exercise or perform daily activities comfortably. These are structural problems caused by
              disproportionate breast mass. Creams, exercises, and non-surgical approaches do not address the underlying cause.
            </p>
            <p>
              Dr. Vicky Jain performs breast reduction surgery for patients whose quality of life is affected by
              breast size, whether the primary concern is physical discomfort, aesthetic proportion, or both. Results are
              immediate in size and symptom relief, with final shape visible at 3 to 6 months as swelling resolves.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Dr. Jain&apos;s Approach to Reduction Mammoplasty
            </h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Breast reduction planning involves two simultaneous goals: removing enough tissue to eliminate symptoms and
                create proportionate size, and reshaping what remains into an aesthetically pleasing contour with the nipple in
                the correct position. These goals must be planned together. Removing tissue without reshaping produces smaller
                but shapeless breasts. Reshaping without adequate reduction fails to relieve the physical symptoms that brought
                the patient to surgery.
              </p>
              <p>
                Dr. Jain uses the pedicle technique to preserve the nipple on a blood-supplied tissue base throughout the
                procedure. The nipple is repositioned without detaching it, which maintains blood flow, sensation, and in most
                cases the ability to breastfeed. The quantity of tissue removed is determined by the patient&apos;s target breast
                size, chest dimensions, and the degree of symptom relief needed. Dr. Jain discusses the planned reduction in grams
                and the expected cup size change during consultation so patients have a concrete understanding of the
                anticipated outcome.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatCorrectsSection() {
  const cards = [
    {
      title: "Chronic Neck and Back Pain",
      icon: Activity,
      copy: "The weight of disproportionately large breasts pulls the cervical and thoracic spine forward continuously. This creates chronic muscle fatigue and joint strain in the neck, upper back, and between the shoulder blades. Reduction removes the weight causing this load and provides lasting pain relief in the majority of patients.",
    },
    {
      title: "Shoulder Groove Indentations",
      icon: Minus,
      copy: "Bra straps bearing significant breast weight cut into the shoulder tissue over time, creating permanent grooves. This is a direct physical consequence of macromastia. Reducing breast weight eliminates the mechanical cause and allows the grooves to improve over time.",
    },
    {
      title: "Skin Rashes and Infections",
      icon: Bandage,
      copy: "The inframammary fold traps heat and moisture where heavy breast tissue rests against the chest wall and upper abdomen. This creates a chronic environment for skin rashes, fungal infections, and skin breakdown. Reducing breast volume eliminates this contact and the conditions that cause it.",
    },
    {
      title: "Restricted Exercise and Physical Activity",
      icon: Dumbbell,
      copy: "Large breasts make high-impact exercise physically uncomfortable and in severe cases functionally limiting. Many patients with macromastia avoid exercise entirely. Reduction removes this restriction and allows patients to engage in activity that was not achievable before surgery.",
    },
    {
      title: "Poor Posture",
      icon: UserRound,
      copy: "The body compensates for anterior breast weight by rounding the shoulders forward and increasing thoracic kyphosis. This postural adaptation is a mechanical response to the load. Reducing breast weight allows the spine to return to a more neutral position over time.",
    },
    {
      title: "Disproportionate Body Appearance",
      icon: Shirt,
      copy: "Breasts that are significantly large relative to the body frame dominate the silhouette and make clothing fit difficult. Reduction brings breast size into proportion with the patient's frame, improving both the aesthetic balance of the body and practical clothing fit.",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="breast-reduction-corrects-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Breast Reduction Surgery Corrects</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]" data-testid={`corrects-card-${index}`}>
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TECHNIQUE_TYPES = [
  {
    title: "Anchor Technique (Inverted-T)",
    body:
      "The most comprehensive reduction technique, appropriate for patients with large breast volume and significant skin excess. Three incisions are used: around the areola, vertically down to the inframammary fold, and horizontally along the fold. This pattern allows maximum tissue removal and the most extensive reshaping. Scar length is greater than other techniques but the horizontal component sits within the natural breast fold and is not visible in clothing.",
  },
  {
    title: "Vertical Technique (Lollipop)",
    body:
      "Appropriate for moderate reductions where the degree of tissue removal and skin excess is less extreme. Two incisions are used: around the areola and vertically down to the fold. Produces less scar length than the anchor technique while still allowing meaningful reshaping. Best for patients who need moderate reduction without the fullest extent of skin removal.",
  },
  {
    title: "Liposuction-Only Reduction",
    body:
      "Appropriate for a specific patient profile: younger patients with good skin elasticity whose breasts are fatty rather than glandular in composition, and who need volume reduction without skin removal or nipple repositioning. Produces minimal scarring but is limited in its ability to reshape or lift. Cannot address ptosis or glandular tissue excess. Patient selection for this technique is critical.",
  },
];

function TechniquesSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="breast-reduction-techniques-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Breast Reduction Surgery</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {TECHNIQUE_TYPES.map((item) => (
            <div key={item.title} className={`bg-white p-8 border border-[#E8E2D9] ${item.title.includes("Liposuction") ? "lg:col-span-2" : ""}`}>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_LIFT_ROWS = [
  {
    label: "Primary Purpose",
    reduction:
      "Remove excess breast tissue, fat, and skin to reduce size and relieve physical symptoms.",
    lift: "Reposition sagging breast tissue and nipple to a more youthful level without significant size change.",
  },
  {
    label: "Tissue Removal",
    reduction: "Significant. The volume and weight of the breast are meaningfully reduced.",
    lift: "Minimal. Skin is removed but breast tissue volume is largely preserved.",
  },
  {
    label: "Symptom Relief",
    reduction: "Yes, directly addresses back pain, shoulder strain, skin rashes, and activity restriction.",
    lift: "Limited. Does not reduce weight that causes physical symptoms.",
  },
  {
    label: "Lift Component",
    reduction:
      "Included in most cases. The breast is reshaped and the nipple repositioned as part of the reduction.",
    lift: "The primary function of the procedure.",
  },
  {
    label: "Best For",
    reduction: "Patients with macromastia causing physical discomfort and aesthetic disproportion.",
    lift: "Patients with ptosis whose breast size is appropriate but position has changed with age, pregnancy, or weight loss.",
  },
];

function VsBreastLiftSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="breast-reduction-vs-lift-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Breast Reduction vs. Breast Lift: Understanding the Difference
        </h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <div
            className="hidden lg:grid lg:grid-cols-3 gap-0 border border-[#E8E2D9] bg-[#E8E2D9] min-w-[680px]"
            role="table"
            aria-label="Breast reduction compared to breast lift"
          >
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 font-medium" role="columnheader" />
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 text-center leading-tight" role="columnheader">
              Breast reduction
            </div>
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 text-center leading-tight" role="columnheader">
              Breast lift
            </div>
            {VS_LIFT_ROWS.map((row) => (
              <React.Fragment key={row.label}>
                <div className="bg-white p-3 text-sm font-medium text-deep-navy border-t border-[#E8E2D9]" role="rowheader">
                  {row.label}
                </div>
                <div className="bg-white p-3 text-sm text-deep-navy/70 leading-relaxed border-t border-[#E8E2D9]">{row.reduction}</div>
                <div className="bg-white p-3 text-sm text-deep-navy/70 leading-relaxed border-t border-[#E8E2D9]">{row.lift}</div>
              </React.Fragment>
            ))}
          </div>
          <div className="hidden md:grid lg:hidden md:grid-cols-2 gap-4">
            {VS_LIFT_ROWS.map((row) => (
              <div key={row.label} className="bg-white p-6 border border-[#E8E2D9] md:col-span-2">
                <h3 className="font-serif text-lg text-premium-purple mb-4">{row.label}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Breast reduction</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.reduction}</p>
                  </div>
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Breast lift</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.lift}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="md:hidden space-y-4">
            {VS_LIFT_ROWS.map((row) => (
              <div key={row.label} className="bg-white p-6 border border-[#E8E2D9]">
                <h3 className="font-serif text-lg text-premium-purple mb-4">{row.label}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Breast reduction</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.reduction}</p>
                  </div>
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Breast lift</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.lift}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-deep-navy/60 text-center mt-8 max-w-3xl mx-auto leading-relaxed">
            Most breast reduction procedures include a mastopexy component. Removing volume while leaving skin excess produces a
            deflated result. Dr. Jain plans the reshaping and lifting as integral parts of the reduction, not as an add-on.
          </p>
        </div>
      </div>
    </section>
  );
}

function HowSurgeryPerformedSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">How Breast Reduction Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 2.5 to 4 hours under general anesthesia depending on breast size and the technique required. Dr.
              Jain marks the breast with the patient upright before surgery, identifying the new nipple position, the skin to be
              removed, and the tissue reduction plan. These markings are precise and form the surgical blueprint.
            </p>
            <p>
              Through the planned incisions, excess breast tissue, fat, and skin are removed. The remaining tissue is reshaped
              into a more projecting, defined breast mound. The nipple-areola complex is repositioned to the marked level on a
              tissue pedicle that preserves blood supply and nerve connections. The skin is closed in layers and the breast is
              dressed in a surgical support bra.
            </p>
            <p>
              Drains are placed in some cases to prevent fluid accumulation during the first 24 to 48 hours. Most patients stay
              one night and go home the following day. The immediate reduction in breast weight is noticeable from the time
              patients wake from anesthesia. Most patients describe relief from their physical symptoms within the first week of
              recovery.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Week 1",
      description:
        "Swelling, bruising, and tightness. The surgical support bra is worn continuously. Most patients notice immediate relief from breast weight. Rest with limited arm movement overhead.",
      icon: BedDouble,
    },
    {
      period: "Week 2",
      description:
        "Most patients return to desk work and light daily activity. External sutures are removed if non-absorbable. Swelling reduces noticeably. The breast shape is already an improvement over before surgery.",
      icon: Monitor,
    },
    {
      period: "Weeks 4 to 6",
      description:
        "Normal daily routine resumes. Strenuous activity and heavy lifting are still restricted. The breast continues to settle into its final shape.",
      icon: Dumbbell,
    },
    {
      period: "Months 2 to 3",
      description:
        "Full activity including exercise resumes. Final breast size, shape, and position are visible as all residual swelling resolves. Scars begin the maturation process that continues over 12 to 18 months.",
      icon: Sparkles,
    },
  ];

  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">
              Who Is a Good Candidate for Breast Reduction?
            </h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Good candidates are women whose breast size causes physical symptoms, aesthetic disproportion, or both, and who
              are in good general health at a stable weight. Age is less of a limiting factor than it is for some procedures.
              Teenagers with severe macromastia causing significant physical symptoms are candidates after breast development is
              complete, typically by age 17 to 18. There is no upper age limit for women in good health.
            </p>
            <p>
              Patients planning future pregnancies should discuss timing with Dr. Jain. Pregnancy can increase breast volume again
              after reduction, though this effect is generally less dramatic than the original macromastia. Patients who have
              completed their family or do not plan to breastfeed have more flexibility in timing. Non-smokers or patients
              willing to stop smoking at least 4 weeks before surgery have significantly lower complication rates, particularly for
              wound healing at the incision sites. Patients who have tried non-surgical approaches including physical therapy,
              specialized bras, and weight loss without adequate symptom relief are appropriate surgical candidates regardless
              of remaining breast size after those interventions.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Breast" />;
}

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {BREAST_REDUCTION_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Breast Reduction Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your breast size, tissue composition, degree of ptosis, and the physical
          symptoms you are experiencing to determine the reduction technique and tissue removal plan that addresses your
          specific anatomy and goals. You leave with a clear understanding of how much can be reduced, what the breast will
          look like afterward, and what recovery involves.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Breast Lift",
      description: "Address breast ptosis in patients whose concern is position rather than size.",
      href: "/breast-lift-surgery-mumbai",
    },
    {
      name: "Liposuction",
      description: "Body contouring to complement improved breast proportion and overall body balance.",
      href: "/liposuction-surgery-mumbai",
    },
    {
      name: "Mommy Makeover",
      description: "Combined post-pregnancy restoration addressing breast, abdomen, and body contour in a single surgery.",
      href: "/mommy-makeover-surgery-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BreastReductionPage() {
  return (
    <>
      <SEO
        title="Breast Reduction Surgery | Dr. Vicky Jain"
        description="Large breasts cause back pain, shoulder strain, and restrict activity. Dr. Vicky Jain performs breast reduction surgery for lasting relief. Book today."
        canonical="/breast-reduction-surgery-mumbai"
      />
      <BreastReductionJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhatCorrectsSection />
      <TechniquesSection />
      <VsBreastLiftSection />
      <HowSurgeryPerformedSection />
      <RecoveryTimelineSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
